import { FC, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Table, Button, Avatar, Typography, Space } from 'antd';
import {
  Appointments_appointmentsForDoctor_data_results as AppointmentResult,
  Appointments_appointmentsForDoctor_data_results as AppointmentType
} from 'gql/appointments/__generated__/Appointments';
import {
  AppointmentStatusType,
  APPOINTMENT_STATUS_VALUES
} from 'constants/appointments';
import { useNavigate } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';

import { getStatusColor } from 'utils/appointments';
import { getFileUrl } from 'utils/file';

const { Title } = Typography;

const PAGE_SIZE = 4;

interface IProps {
  data: AppointmentResult[];
}

const TabItem: FC<IProps> = ({ data = [] }) => {
  // translations
  const { t } = useTranslation();
  // states
  const [page, setPage] = useState(0);
  // navigation
  const navigate = useNavigate();

  const pageUnit = page * PAGE_SIZE;

  const columns: ColumnProps<AppointmentType>[] = useMemo(
    () => [
      {
        title: t('table.name'),
        dataIndex: 'patient',
        key: 'patient',
        render: data => (
          <Space align="center">
            <Avatar
              src={getFileUrl(data.avatar)}
              size={32}
              style={{ marginRight: 0 }}
            />
            <Title level={5} style={{ margin: 0 }}>
              {data.first_name} {data.last_name}
            </Title>
          </Space>
        )
      },
      {
        title: t('dashboard.plan'),
        dataIndex: 'plan',
        key: 'plan',
        render: value => value.name
      },
      {
        title: t('appointments.status'),
        dataIndex: 'status',
        key: 'status',
        render: (value: AppointmentStatusType) => (
          <Tag color={getStatusColor(value)}>
            {t(`appointments.${APPOINTMENT_STATUS_VALUES[value]}`)}
          </Tag>
        )
      },
      {
        title: t('dashboard.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        render: value => dayjs(value).format(DATE_FORMAT)
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: id => (
          <Button
            onClick={() => navigate(`/appointments/details/${id}`)}
            type="link"
          >
            {t('appointments.view_appointment')}
          </Button>
        )
      }
    ],
    [navigate, t]
  );

  return (
    <Table
      dataSource={data.slice(pageUnit, pageUnit + PAGE_SIZE)}
      columns={columns}
      pagination={{
        total: data.length,
        pageSize: PAGE_SIZE,
        onChange: v => setPage(v - 1),
        current: page + 1
      }}
      rowKey="id"
      scroll={{ x: 782 }}
    />
  );
};

export default memo(TabItem);
